import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShieldOff } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/data/EmptyState'
import { SybilFilters } from '@/components/mlro/SybilFilters'
import { SybilClusterQueue } from '@/components/mlro/SybilClusterQueue'
import { useRoles } from '@/hooks/useRoles'
import { SYBIL_CLUSTER_STATUSES, type SybilClusterListFilters } from '@/types/sybil'
import { searchEnum } from '@/lib/search-params'

export const Route = createFileRoute('/mlro/sybil/')({
    component: MlroSybilQueuePage,
    validateSearch: (search): SybilClusterListFilters => ({
        status: searchEnum(search.status, SYBIL_CLUSTER_STATUSES),
    }),
})

function MlroSybilQueuePage() {
    const filters = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { hasAnyRole } = useRoles()

    if (!hasAnyRole('mlro')) {
        return (
            <EmptyState
                icon={ShieldOff}
                title="No access"
                description="The MLRO surfaces are restricted to operators with the MLRO role."
            />
        )
    }

    const setFilters = (next: SybilClusterListFilters) => {
        navigate({ search: () => prune(next), replace: true })
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Sybil clusters</h1>
                <p className="text-muted-foreground text-sm">
                    Wallet groupings flagged by the correlation engine. Clusters of three reach
                    review; five trigger an automatic block via the pre-tx gate.
                </p>
            </div>

            <SybilFilters value={filters} onChange={setFilters} />

            <Card>
                <CardHeader>
                    <CardTitle>Queue</CardTitle>
                </CardHeader>
                <CardContent>
                    <SybilClusterQueue filters={filters} />
                </CardContent>
            </Card>
        </div>
    )
}

function prune(filters: SybilClusterListFilters): SybilClusterListFilters {
    const out: SybilClusterListFilters = {}
    if (filters.status) out.status = filters.status
    return out
}
